import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, CheckCircle2, Trash2, Loader2, ExternalLink, RefreshCw, Mail, Clock } from 'lucide-react';
import { listOrders, markOrderPaid, deleteOrder, resendOrderReceipts, getToken, clearToken, me } from '../lib/api';
import { ReceiptStatus } from '../components/ReceiptStatus';
import { useToast } from '../hooks/use-toast';
import { iceLogo, invoiceEmail } from '../mock';

export default function AdminOrders() {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [authOk, setAuthOk] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrders(await listOrders());
    } catch (e) {
      toast({ title: 'Error loading orders', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  const markPaid = async (id) => {
    setBusyId(id);
    try {
      await markOrderPaid(id);
      toast({ title: 'Order marked as paid' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  };

  const resendReceipts = async (id) => {
    setBusyId(id);
    try {
      const { receipts } = await resendOrderReceipts(id);
      const failed = Object.values(receipts || {}).filter((r) => r.status === 'failed');
      toast({ title: failed.length ? 'Receipt could not be sent' : 'Receipt sent', description: failed.length ? failed.map((r) => r.error).join('; ') : 'Itemized receipt emailed to you and the buyer.' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    setBusyId(id);
    try {
      await deleteOrder(id);
      toast({ title: 'Order deleted' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  };

  const invoiceEmailUrl = (o) => {
    const subject = encodeURIComponent(`Invoice ${o.invoiceNumber} — ${o.productName}`);
    const bodyLines = [
      `Invoice: ${o.invoiceNumber}`,
      `Product: ${o.productName}${o.variant ? ` — ${o.variant}` : ''}${o.size ? ` — Size ${o.size}` : ''}`,
      `Qty: ${o.quantity}`,
      o.unitPrice ? `Unit Price: $${o.unitPrice}` : 'Unit Price: TBA',
      o.total ? `Total: $${o.total}` : 'Total: TBA',
      '',
      `Buyer: ${o.customerName}`,
      `Email: ${o.customerEmail}`,
      o.customerPhone ? `Phone: ${o.customerPhone}` : '',
      o.notes ? `Notes: ${o.notes}` : '',
      '',
      'Pay via Autobook: https://app.autobook.co',
      '',
      '— Brookline JP Patriots'
    ].filter(Boolean).join('\n');
    return `mailto:${invoiceEmail}?cc=${encodeURIComponent(o.customerEmail)}&subject=${subject}&body=${encodeURIComponent(bodyLines)}`;
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  const pendingCount = orders.filter((o) => o.status !== 'paid').length;
  const paidCount = orders.filter((o) => o.status === 'paid').length;
  const revenue = orders.filter((o) => o.status === 'paid').reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      {/* Header */}
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div>
              <div className="font-display text-2xl leading-none mt-1">APPAREL ORDERS</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/scholars" data-testid="orders-nav-scholars" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Scholars</Link>
            <Link to="/admin/inventory" data-testid="orders-nav-inventory" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Inventory</Link>
            <Link to="/admin/season" data-testid="orders-nav-season" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Season</Link>
            <Link to="/admin/announcements" data-testid="orders-nav-announcements" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Announcements</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Change Password</Link>
            <button
              onClick={() => { clearToken(); window.location.href = '/admin/login'; }}
              data-testid="orders-logout"
              className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20"
            >
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Stats + refresh */}
      <section className="bg-white border-b border-[#0a1f44]/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center gap-6">
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Total Orders</div>
            <div className="font-display text-3xl text-[#0a1f44]" data-testid="orders-total">{orders.length}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Pending</div>
            <div className="font-display text-3xl text-[#c8102e]" data-testid="orders-pending">{pendingCount}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Paid</div>
            <div className="font-display text-3xl text-emerald-700" data-testid="orders-paid">{paidCount}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Revenue</div>
            <div className="font-display text-3xl text-[#0a1f44]" data-testid="orders-revenue">${revenue.toFixed(2)}</div>
          </div>
          <button
            onClick={load}
            className="ml-auto inline-flex items-center gap-2 border border-[#0a1f44]/20 text-[#0a1f44] font-heading uppercase tracking-widest text-[10px] px-4 py-2 hover:bg-[#0a1f44] hover:text-white transition-colors"
            data-testid="orders-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </section>

      {/* Orders table */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="p-16 text-center text-[#0a1f44]/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="p-16 text-center bg-white border border-[#0a1f44]/10">
            <div className="font-display text-2xl text-[#0a1f44] mb-2">NO ORDERS YET</div>
            <p className="text-[#0a1f44]/60 text-sm">Once families submit orders from the /apparel page, they'll appear here for you to invoice.</p>
          </div>
        ) : (
          <>
          <div className="md:hidden space-y-3" data-testid="orders-mobile-list">
            {orders.map((order) => (
              <article key={order.id} className="bg-white border border-[#0a1f44]/10 p-4" data-testid={`order-mobile-card-${order.invoiceNumber}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><div className="font-display text-xl text-[#0a1f44]">{order.invoiceNumber}</div><div className="text-xs text-[#0a1f44]/50 mt-1">{new Date(order.createdAt).toLocaleDateString()}</div></div>
                  <span className={`shrink-0 inline-flex items-center gap-1 font-heading uppercase text-[9px] px-2 py-1 ${order.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-[#c8102e]/10 text-[#c8102e]'}`}>{order.status === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{order.status}</span>
                </div>
                <div className="mt-3"><ReceiptStatus order={order} busy={busyId === order.id} onResend={resendReceipts} compact /></div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-[10px] text-[#0a1f44]/50 font-heading uppercase">Product</div><div className="font-heading text-[#0a1f44] break-words">{order.productName}</div><div className="text-xs text-[#0a1f44]/60">{[order.variant, order.size].filter(Boolean).join(' · ')}</div></div>
                  <div><div className="text-[10px] text-[#0a1f44]/50 font-heading uppercase">Total</div><div className="font-display text-xl text-[#c8102e]">{order.total ? `$${order.total.toFixed(2)}` : 'TBA'}</div><div className="text-xs text-[#0a1f44]/60">Qty {order.quantity}</div></div>
                  <div className="col-span-2"><div className="text-[10px] text-[#0a1f44]/50 font-heading uppercase">Buyer</div><div className="font-heading text-[#0a1f44]">{order.customerName}</div><a href={`mailto:${order.customerEmail}`} data-testid={`order-mobile-email-${order.invoiceNumber}`} className="text-sm text-[#0a1f44]/70 break-all">{order.customerEmail}</a>{order.customerPhone && <a href={`tel:${order.customerPhone}`} data-testid={`order-mobile-phone-${order.invoiceNumber}`} className="block text-sm text-[#0a1f44]/70">{order.customerPhone}</a>}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 border-t border-[#0a1f44]/10 pt-3">
                  <a href={invoiceEmailUrl(order)} data-testid={`order-mobile-invoice-${order.invoiceNumber}`} className="min-h-11 inline-flex items-center justify-center gap-2 border border-[#0a1f44]/20 px-3 text-[#0a1f44] font-heading uppercase text-[9px]"><Mail className="w-4 h-4" /> Invoice</a>
                  {order.status !== 'paid' && <button onClick={() => markPaid(order.id)} disabled={busyId === order.id} data-testid={`order-mobile-paid-${order.invoiceNumber}`} className="min-h-11 inline-flex items-center gap-2 bg-emerald-700 text-white px-3 font-heading uppercase text-[9px]"><CheckCircle2 className="w-4 h-4" /> Mark Paid</button>}
                  <button onClick={() => remove(order.id)} disabled={busyId === order.id} data-testid={`order-mobile-delete-${order.invoiceNumber}`} className="w-11 h-11 flex items-center justify-center border border-[#c8102e] text-[#c8102e]" aria-label={`Delete ${order.invoiceNumber}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </article>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto bg-white border border-[#0a1f44]/10">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-[#0a1f44] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Invoice</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Product</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Buyer</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Qty</th>
                  <th className="px-3 py-3 text-right font-heading uppercase tracking-widest text-[10px]">Total</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Status</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.invoiceNumber}`} className="border-t border-[#0a1f44]/5">
                    <td className="px-3 py-3">
                      <div className="font-display text-[#0a1f44]">{o.invoiceNumber}</div>
                      <div className="text-[10px] text-[#0a1f44]/50 font-heading uppercase tracking-widest flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(o.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-heading text-[#0a1f44]">{o.productName}</div>
                      <div className="text-xs text-[#0a1f44]/60">{[o.variant, o.size].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-heading text-[#0a1f44]">{o.customerName}</div>
                      <div className="text-xs text-[#0a1f44]/60">{o.customerEmail}</div>
                      {o.customerPhone && <div className="text-xs text-[#0a1f44]/60">{o.customerPhone}</div>}
                      {o.notes && <div className="mt-1 text-xs italic text-[#0a1f44]/50">"{o.notes}"</div>}
                    </td>
                    <td className="px-3 py-3 text-center font-heading">{o.quantity}</td>
                    <td className="px-3 py-3 text-right font-display text-[#0a1f44]">
                      {o.total ? `$${o.total.toFixed(2)}` : <span className="text-[#0a1f44]/40 text-xs">TBA</span>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {o.status === 'paid' ? (
                        <div className="space-y-1.5">
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 font-heading uppercase tracking-widest text-[9px] px-2 py-1">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </span>
                          <ReceiptStatus order={o} busy={busyId === o.id} onResend={resendReceipts} />
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-[#c8102e]/10 text-[#c8102e] font-heading uppercase tracking-widest text-[9px] px-2 py-1">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <a
                          href={invoiceEmailUrl(o)}
                          data-testid={`order-invoice-btn-${o.invoiceNumber}`}
                          title="Send invoice email (opens mail draft to jmadstorm@gmail.com)"
                          className="p-2 text-[#0a1f44]/60 hover:text-[#c8102e] hover:bg-[#c8102e]/10 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                        {o.status !== 'paid' && (
                          <button
                            onClick={() => markPaid(o.id)}
                            disabled={busyId === o.id}
                            data-testid={`order-mark-paid-${o.invoiceNumber}`}
                            title="Mark as paid"
                            className="p-2 text-emerald-700 hover:text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <a
                          href="https://app.autobook.co"
                          target="_blank"
                          rel="noreferrer"
                          title="Open Autobook"
                          className="p-2 text-[#0a1f44]/60 hover:text-[#0a1f44] transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => remove(o.id)}
                          disabled={busyId === o.id}
                          data-testid={`order-delete-${o.invoiceNumber}`}
                          title="Delete order"
                          className="p-2 text-[#c8102e]/60 hover:text-white hover:bg-[#c8102e] transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>
    </div>
  );
}
